import * as React from 'react';
import { startTransition } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { api } from './api';
import { createBudgetDraft, createItem, defaultSyncConfig, monthOptions, sectionConfig } from './plannerDefaults';
import { usePlannerActions } from './PlannerActionContext';
import { formatMoney, formatTimestamp, computeExpenseAnalytics } from './shared';

const dashboardStorageKey = 'fbp.dashboard.widgets.v1';

const dashboardWidgetCatalog = {
  kpi: {
    title: 'Ключевые показатели',
    description: 'Плановый и фактический итог года.',
  },
  cashflow: {
    title: 'Приходы и расходы',
    description: 'План/факт по доходам и расходам за каждый месяц.',
  },
  pulse: {
    title: 'Пульс бюджета',
    description: 'Расходы к плану и количество месяцев с перерасходом.',
  },
  checklist: {
    title: 'Что требует внимания',
    description: 'Риски по доходам, расходам и синхронизации.',
  },
  monthlyVariance: {
    title: 'Отклонения по месяцам',
    description: 'Мини-графики отклонений расходов и остатка.',
  },
};

const defaultDashboardWidgets = ['kpi', 'cashflow', 'pulse', 'checklist', 'monthlyVariance'];

function loadDashboardWidgets() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(dashboardStorageKey) || '[]');
    const valid = saved.filter((id) => dashboardWidgetCatalog[id]);
    if (valid.length > 0) {
      return valid;
    }
  } catch (storageError) {
    // Ignore malformed local dashboard preferences and fall back to defaults.
  }
  return defaultDashboardWidgets;
}

function normalizeDetail(detail) {
  return {
    ...detail,
    budget: {
      ...detail.budget,
      currency_code: detail.budget.currency_code || 'RUB',
      notes: detail.budget.notes || '',
    },
    items: (detail.items || []).map((item, index) => ({
      ...item,
      amount: item.amount ?? '0',
      month: item.month ?? null,
      start_month: item.start_month ?? null,
      end_month: item.end_month ?? null,
      sort_order: item.sort_order || index + 1,
      firefly_category_id: item.firefly_category_id ?? null,
      firefly_category_ids: item.firefly_category_ids || (item.firefly_category_id ? [item.firefly_category_id] : []),
      firefly_budget_id: item.firefly_budget_id ?? null,
      firefly_account_id: item.firefly_account_id ?? null,
	  income_type: item.kind === 'income' ? (item.income_type || 'salary') : '',
	  income_owner: item.income_owner || '',
      firefly_bill_id: item.firefly_bill_id ?? null,
      firefly_rule_ids: item.firefly_rule_ids || [],
      create_firefly_bill: Boolean(item.create_firefly_bill),
      sync_to_firefly: Boolean(item.sync_to_firefly),
      last_synced_at: item.last_synced_at || null,
      last_sync_status: item.last_sync_status || '',
      last_sync_summary: item.last_sync_summary || '',
    })),
    monthly_plan: detail.monthly_plan || [],
    item_summaries: detail.item_summaries || [],
    firefly_syncable: detail.firefly_syncable || [],
    sync_config: {
      ...defaultSyncConfig,
      ...(detail.sync_config || {}),
    },
    sync_runs: detail.sync_runs || [],
  };
}

export default function PlannerWorkspace() {
  const { selectedYear, selectedBudgetId, refreshBudgets } = usePlannerActions();
  const [references, setReferences] = React.useState({
    categories: [],
    accounts: [],
    budgets: [],
    bills: [],
    rules: [],
  });
  const [draft, setDraft] = React.useState(createBudgetDraft(selectedYear));
  const [dashboardWidgets, setDashboardWidgets] = React.useState(loadDashboardWidgets);
  const [isDashboardEditorOpen, setIsDashboardEditorOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
	const [refreshingFirefly, setRefreshingFirefly] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const referenceData = await api.getPlannerReference();
        if (cancelled) return;
        setReferences(referenceData);
      } catch (loadError) {
        if (!cancelled) setError(loadError.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (!selectedBudgetId) {
      return;
    }
    let cancelled = false;
    async function loadBudget() {
      try {
        setLoading(true);
        const detail = await api.getPlannerBudget(selectedBudgetId);
        if (!cancelled) {
          startTransition(() => {
            setDraft(normalizeDetail(detail));
            setError('');
          });
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    loadBudget();
    return () => {
      cancelled = true;
    };
  }, [selectedBudgetId]);

  React.useEffect(() => {
    window.localStorage.setItem(dashboardStorageKey, JSON.stringify(dashboardWidgets));
  }, [dashboardWidgets]);

  function addItem(kind) {
    setDraft((current) => ({
      ...current,
      items: [...current.items, createItem(kind, current.items.length + 1)],
    }));
  }

  function updateItem(index, field, value) {
    setDraft((current) => {
      const items = current.items.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }
        const nextItem = { ...item, [field]: value };
        if (field === 'frequency') {
          if (value === 'monthly') {
            nextItem.month = null;
            nextItem.start_month = null;
            nextItem.end_month = null;
          } else if (value === 'range') {
            nextItem.month = null;
            nextItem.start_month = nextItem.start_month ?? 1;
            nextItem.end_month = nextItem.end_month ?? 12;
          } else if (value === 'once') {
            nextItem.start_month = null;
            nextItem.end_month = null;
          }
        }
        if (field === 'kind') {
          const defaults = sectionConfig[value];
          nextItem.frequency = defaults.defaultFrequency;
          nextItem.sync_to_firefly = defaults.defaultSyncToFirefly;
          nextItem.month = defaults.defaultFrequency === 'once' ? (nextItem.month || 1) : null;
        }
		if (field === 'income_type') {
		  if (value === 'bonus') {
			nextItem.frequency = 'once';
			nextItem.month = nextItem.month || 1;
		  } else if (item.income_type === 'bonus') {
			nextItem.frequency = 'monthly';
			nextItem.month = null;
		  }
		}
        if (field === 'firefly_category_ids') {
          nextItem.firefly_category_id = value.length > 0 ? value[0] : null;
        }
        if (field === 'sync_to_firefly') {
          if (!value) {
            nextItem.create_firefly_bill = item.kind === 'one_time' ? true : item.create_firefly_bill;
          } else {
            nextItem.create_firefly_bill = true;
          }
        }
        return nextItem;
      });
      return { ...current, items };
    });
  }

  function removeItem(index) {
    setDraft((current) => ({
      ...current,
      items: current.items.filter((_, itemIndex) => itemIndex !== index),
    }));
  }

  function buildSavePayload(sourceDraft) {
    return {
      id: sourceDraft.budget.id || 0,
      year: Number(sourceDraft.budget.year),
      currency_code: sourceDraft.budget.currency_code || 'RUB',
      notes: sourceDraft.budget.notes || '',
      sync_config: sourceDraft.sync_config,
      items: sourceDraft.items
        .filter((item) => item.name.trim())
        .map((item, index) => ({
          ...item,
          id: item.id || 0,
          amount: item.amount || '0',
          month: item.frequency === 'once' ? Number(item.month || 1) : null,
          start_month: item.frequency === 'range' && item.start_month != null ? Number(item.start_month) : null,
          end_month: item.frequency === 'range' && item.end_month != null ? Number(item.end_month) : null,
          firefly_category_id: item.firefly_category_ids.length > 0 ? Number(item.firefly_category_ids[0]) : null,
          firefly_category_ids: item.firefly_category_ids.map(Number),
          firefly_budget_id: item.firefly_budget_id ? Number(item.firefly_budget_id) : null,
          firefly_account_id: item.firefly_account_id ? Number(item.firefly_account_id) : null,
          firefly_bill_id: item.firefly_bill_id ? Number(item.firefly_bill_id) : null,
          firefly_rule_ids: (item.firefly_rule_ids || []).map(Number),
          create_firefly_bill: item.kind === 'one_time' ? true : Boolean(item.create_firefly_bill),
          sync_to_firefly: item.kind === 'income' ? false : Boolean(item.sync_to_firefly),
          sort_order: index + 1,
        })),
    };
  }

  async function saveDraft(sourceDraft = draft) {
    const saved = await api.savePlannerBudget(buildSavePayload(sourceDraft));
    const normalized = normalizeDetail(saved);
    setDraft(normalized);
    await refreshBudgets();
    const referenceData = await api.getPlannerReference();
    setReferences(referenceData);
    return normalized;
  }

  async function refreshAfterServerChange(budgetId) {
    const [detail, referenceData] = await Promise.all([
      api.getPlannerBudget(budgetId),
      api.getPlannerReference(),
    ]);
    setDraft(normalizeDetail(detail));
    setReferences(referenceData);
    await refreshBudgets();
  }

  async function saveBudget() {
    try {
      setSaving(true);
      setMessage('');
      setError('');
      await saveDraft();
      setMessage('Бюджет сохранен.');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  }

  async function syncBudget() {
    try {
      setSyncing(true);
      setError('');
      setMessage('Сохраняю изменения перед синхронизацией...');
      const saved = await saveDraft(draft);
      setMessage('Синхронизирую с Firefly III...');
      try {
        await api.syncPlannerBudget(saved.budget.id, { sync_config: saved.sync_config });
        await refreshAfterServerChange(saved.budget.id);
        setMessage('Синхронизация завершена успешно.');
      } catch (syncError) {
        console.error('Ошибка синхронизации:', syncError);
        setError(`Ошибка синхронизации: ${syncError.message || 'Неизвестная ошибка'}`);
      }
    } catch (saveError) {
      console.error('Ошибка сохранения:', saveError);
      setError(`Ошибка сохранения: ${saveError.message || 'Неизвестная ошибка'}`);
    } finally {
      setSyncing(false);
    }
  }

	async function getFireflyData() {
	  if (!selectedBudgetId) {
		setError('Сначала сохраните и выберите бюджет.');
		return;
	  }
	  try {
		setRefreshingFirefly(true);
		setError('');
		setMessage('Получаю фактические данные из Firefly III...');
		const detail = await api.refreshPlannerBudget(selectedBudgetId);
		setDraft(normalizeDetail(detail));
		setMessage('Фактические данные Firefly III обновлены.');
	  } catch (refreshError) {
		setError(`Не удалось получить данные Firefly III: ${refreshError.message}`);
	  } finally {
		setRefreshingFirefly(false);
	  }
	}

  const actionsRef = React.useRef({ saveBudget, syncBudget, getFireflyData });
  actionsRef.current = { saveBudget, syncBudget, getFireflyData };

  const { setNavActions } = usePlannerActions();

  React.useEffect(() => {
    setNavActions({
      saveBudget: () => actionsRef.current.saveBudget(),
      syncBudget: () => actionsRef.current.syncBudget(),
      getFireflyData: () => actionsRef.current.getFireflyData(),
      saving,
      syncing,
      refreshingFirefly,
      selectedBudgetId,
    });
  }, [saving, syncing, refreshingFirefly, selectedBudgetId, setNavActions]);

  function addDashboardWidget(widgetId) {
    if (!widgetId || dashboardWidgets.includes(widgetId)) {
      return;
    }
    setDashboardWidgets((current) => [...current, widgetId]);
  }

  function removeDashboardWidget(widgetId) {
    setDashboardWidgets((current) => current.filter((id) => id !== widgetId));
  }

  function moveDashboardWidget(widgetId, direction) {
    setDashboardWidgets((current) => {
      const index = current.indexOf(widgetId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) {
        return current;
      }
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  }

  function resetDashboardWidgets() {
    setDashboardWidgets(defaultDashboardWidgets);
    setError('');
  }

  const itemsByKind = React.useMemo(
    () =>
      draft.items.reduce((result, item, index) => {
        const key = item.kind || 'recurring';
        if (!result[key]) {
          result[key] = [];
        }
        result[key].push({ item, index });
        return result;
      }, {}),
    [draft.items],
  );

  const quickStats = React.useMemo(() => {
    const rows = draft.monthly_plan || [];
    const plannedIncome = rows.reduce((sum, row) => sum + Number(row.planned_income || 0), 0);
    const plannedExpense = rows.reduce((sum, row) => sum + Number(row.planned_expenses || 0), 0);
    const actualIncome = rows.reduce((sum, row) => sum + Number(row.actual_income || 0), 0);
    const actualExpense = rows.reduce((sum, row) => sum + Number(row.actual_expenses || 0), 0);
    return {
      plannedIncome,
      plannedExpense,
      plannedBalance: plannedIncome - plannedExpense,
      actualIncome,
      actualExpense,
      actualBalance: actualIncome - actualExpense,
      expenseOverrunMonths: rows.filter((row) => Number(row.actual_expenses || 0) > Number(row.planned_expenses || 0)).length,
      incomeLagMonths: rows.filter((row) => Number(row.actual_income || 0) < Number(row.planned_income || 0)).length,
      worstMonth: rows.reduce(
        (worst, row) => (Number(row.balance_variance || 0) < Number(worst?.balance_variance || 0) ? row : worst),
        rows[0] || null,
      ),
    };
  }, [draft.monthly_plan]);

  if (loading) {
    return (
      <main className="planner-page">
        <p className="empty-state">Загружаю бюджет...</p>
      </main>
    );
  }

  return (
    <main className="planner-page">
      {(message || error) && (
        <section className={`message-banner ${error ? 'error' : 'success'}`}>{error || message}</section>
      )}

        <DashboardWorkspace
          widgets={dashboardWidgets}
          stats={quickStats}
          rows={draft.monthly_plan || []}
          summaries={draft.item_summaries || []}
          syncItems={draft.items.filter((item) => item.sync_to_firefly).length}
          currencyCode={draft.budget.currency_code}
          isEditorOpen={isDashboardEditorOpen}
          onToggleEditor={() => setIsDashboardEditorOpen((current) => !current)}
          onAddWidget={addDashboardWidget}
          onRemoveWidget={removeDashboardWidget}
          onMoveWidget={moveDashboardWidget}
          onResetWidgets={resetDashboardWidgets}
        />

	  <IncomePlanFactSummary summaries={draft.item_summaries || []} currencyCode={draft.budget.currency_code} />

      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Редактор</p>
            <h2>Статьи бюджета</h2>
          </div>
        </div>

        {Object.keys(sectionConfig).map((kind) => (
          <SectionTable
            key={kind}
            kind={kind}
            rows={itemsByKind[kind] || []}
            references={references}
            onAdd={() => addItem(kind)}
            onUpdate={updateItem}
            onRemove={removeItem}
          />
        ))}
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">План</p>
            <h2>План доходов и расходов</h2>
          </div>
        </div>
        <PlanLineChart rows={draft.monthly_plan || []} currencyCode={draft.budget.currency_code} />
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Plan-Fact</p>
            <h2>Помесячная аналитика</h2>
          </div>
        </div>
        <MonthlyPlanTable rows={draft.monthly_plan || []} currencyCode={draft.budget.currency_code} />
      </section>

    </main>
  );
}

function DashboardWorkspace({
  widgets,
  stats,
  rows,
  summaries,
  syncItems,
  currencyCode,
  isEditorOpen,
  onToggleEditor,
  onAddWidget,
  onRemoveWidget,
  onMoveWidget,
  onResetWidgets,
}) {
  const hiddenWidgets = Object.keys(dashboardWidgetCatalog).filter((id) => !widgets.includes(id));
  return (
    <section className="dashboard-workspace">
      <div className="dashboard-toolbar">
        <div>
          <p className="panel-kicker">Dashboard</p>
          <h2>Сводка бюджета</h2>
        </div>
        <button type="button" className="ghost-button" onClick={onToggleEditor}>
          {isEditorOpen ? 'Закрыть настройку' : 'Настроить дашборд'}
        </button>
      </div>

      {isEditorOpen && (
        <article className="dashboard-editor panel">
          <div className="dashboard-editor-head">
            <div>
              <h3>Виджеты</h3>
              <p>Можно скрывать, возвращать и менять порядок. Настройка хранится локально в браузере.</p>
            </div>
            <button type="button" className="link-button" onClick={onResetWidgets}>
              Сбросить
            </button>
          </div>
          <div className="widget-config-list">
            {widgets.map((widgetId, index) => (
              <WidgetConfigRow
                key={widgetId}
                widgetId={widgetId}
                index={index}
                total={widgets.length}
                onMove={onMoveWidget}
                onRemove={onRemoveWidget}
              />
            ))}
          </div>
          <label className="field compact-field add-widget-field">
            <span>Добавить виджет</span>
            <select value="" onChange={(event) => onAddWidget(event.target.value)} disabled={hiddenWidgets.length === 0}>
              <option value="">{hiddenWidgets.length === 0 ? 'Все виджеты уже добавлены' : 'Выберите виджет'}</option>
              {hiddenWidgets.map((widgetId) => (
                <option key={widgetId} value={widgetId}>
                  {dashboardWidgetCatalog[widgetId].title}
                </option>
              ))}
            </select>
          </label>
        </article>
      )}

      <div className="dashboard-widget-grid">
        {widgets.map((widgetId) => (
          <DashboardWidget key={widgetId} widgetId={widgetId} stats={stats} rows={rows} summaries={summaries} syncItems={syncItems} currencyCode={currencyCode} />
        ))}
      </div>
    </section>
  );
}

function WidgetConfigRow({ widgetId, index, total, onMove, onRemove }) {
  const widget = dashboardWidgetCatalog[widgetId];
  return (
    <div className="widget-config-row">
      <div>
        <strong>{widget.title}</strong>
        <span>{widget.description}</span>
      </div>
      <div className="widget-config-actions">
        <button type="button" className="ghost-button" onClick={() => onMove(widgetId, -1)} disabled={index === 0}>
          Выше
        </button>
        <button type="button" className="ghost-button" onClick={() => onMove(widgetId, 1)} disabled={index === total - 1}>
          Ниже
        </button>
        <button type="button" className="link-button" onClick={() => onRemove(widgetId)}>
          Скрыть
        </button>
      </div>
    </div>
  );
}

function DashboardWidget({ widgetId, stats, rows, summaries, syncItems, currencyCode }) {
  switch (widgetId) {
    case 'kpi':
      return (
        <section className="stats-grid dashboard-widget dashboard-widget-wide">
          <StatCard title="Плановый доход" value={stats.plannedIncome} tone="income" currencyCode={currencyCode} />
          <StatCard title="Плановые расходы" value={stats.plannedExpense} tone="expense" currencyCode={currencyCode} />
          <StatCard title="Плановый баланс" value={stats.plannedBalance} tone="neutral" currencyCode={currencyCode} />
          <StatCard title="Фактический баланс" value={stats.actualBalance} tone="neutral" currencyCode={currencyCode} />
        </section>
      );
    case 'cashflow':
      return (
        <article className="panel chart-panel dashboard-widget dashboard-widget-wide">
          <div className="panel-header">
            <div>
              <p className="panel-kicker">Cashflow</p>
              <h2>График приходов и расходов</h2>
            </div>
          </div>
          <CashflowChart rows={rows} currencyCode={currencyCode} />
        </article>
      );
    case 'pulse':
      return <BudgetPulse stats={stats} rows={rows} summaries={summaries} currencyCode={currencyCode} />;
    case 'checklist':
      return (
        <article className="panel panel-accent dashboard-widget">
          <div className="panel-header">
            <div>
              <p className="panel-kicker">Контроль</p>
              <h2>Что требует внимания</h2>
            </div>
          </div>
          <BudgetChecklist stats={stats} rows={rows} syncItems={syncItems} currencyCode={currencyCode} />
        </article>
      );
    case 'monthlyVariance':
      return (
        <article className="panel dashboard-widget dashboard-widget-wide">
          <div className="panel-header">
            <div>
              <p className="panel-kicker">Plan-Fact</p>
              <h2>Отклонения по месяцам</h2>
            </div>
          </div>
          <MonthlyAnalyticsCharts rows={rows} />
        </article>
      );
    default:
      return null;
  }
}

function SectionTable({ kind, rows, references, onAdd, onUpdate, onRemove }) {
  const config = sectionConfig[kind];

  return (
    <section className="section-block">
      <div className="section-header">
        <div>
          <h3>{config.title}</h3>
          <p>{config.description}</p>
        </div>
        <div className="section-actions">
          <button type="button" className="ghost-button" onClick={onAdd}>
            <AddCircleOutlineIcon fontSize="small" /> Добавить строку
          </button>
        </div>
      </div>

      <div className="table-scroll">
        <table className="planner-table planner-table-wide">
          <thead>
            <tr>
              <th>Название</th>
			  {kind === 'income' && <th>Тип дохода</th>}
			  {kind === 'income' && <th>Пользователь</th>}
			  {kind === 'income' && <th>Счет дохода</th>}
              <th>Частота</th>
              <th>Месяц</th>
              <th>Сумма</th>
              {kind !== 'income' && <th>Sync</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map(({ item, index }) => {
              return (
                <tr key={`${kind}-${index}`}>
                  <td>
                    <input
                      type="text"
                      value={item.name}
                      onChange={(event) => onUpdate(index, 'name', event.target.value)}
                      placeholder="Название статьи"
                    />
                    {item.last_synced_at && (
                      <div className="inline-meta">
                        <strong>{item.last_sync_status || 'synced'}</strong>
                        <span>{formatTimestamp(item.last_synced_at)}</span>
                        <small>{item.last_sync_summary}</small>
                      </div>
                    )}
                  </td>
				  {kind === 'income' && (
					<td>
					  <select value={item.income_type || 'salary'} onChange={(event) => onUpdate(index, 'income_type', event.target.value)}>
						<option value="salary">Зарплата</option>
						<option value="bonus">Премия</option>
						<option value="other">Другое</option>
					  </select>
					</td>
				  )}
				  {kind === 'income' && (
					<td>
					  <input
						type="text"
						value={item.income_owner || ''}
						onChange={(event) => onUpdate(index, 'income_owner', event.target.value)}
						placeholder="Владелец счета"
					  />
					</td>
				  )}
				  {kind === 'income' && (
					<td>
					  <select value={item.firefly_account_id || ''} onChange={(event) => onUpdate(index, 'firefly_account_id', Number(event.target.value) || null)}>
						<option value="">Выберите счет</option>
						{references.accounts
						  .filter((account) => account.attributes?.type === 'asset')
						  .map((account) => (
							<option key={account.id} value={account.id}>{account.attributes?.name}</option>
						  ))}
					  </select>
					</td>
				  )}
                  <td>
                    <select value={item.frequency} disabled={kind === 'income' && item.income_type === 'bonus'} onChange={(event) => onUpdate(index, 'frequency', event.target.value)}>
                      <option value="monthly">Ежемесячно</option>
                      <option value="once">Один раз</option>
                      {(kind === 'income' || kind === 'recurring') && <option value="range">Другое</option>}
                    </select>
                  </td>
                  <td>
                    {item.frequency === 'range' && (kind === 'income' || kind === 'recurring') ? (
                      <MonthRangePicker
                        startMonth={item.start_month}
                        endMonth={item.end_month}
                        onChange={(start, end) => {
                          onUpdate(index, 'start_month', start);
                          onUpdate(index, 'end_month', end);
                        }}
                      />
                    ) : (
                      <select
                        value={item.month ?? ''}
                        disabled={item.frequency !== 'once'}
                        onChange={(event) => onUpdate(index, 'month', Number(event.target.value))}
                      >
                        <option value="">—</option>
                        {monthOptions.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="amount-cell">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={item.amount}
                      onChange={(event) => onUpdate(index, 'amount', event.target.value)}
                      className="amount-input"
                    />
                  </td>
                  {kind !== 'income' && (
                    <td className="checkbox-cell">
                      <input
                        type="checkbox"
                        checked={Boolean(item.sync_to_firefly)}
                        onChange={(event) => onUpdate(index, 'sync_to_firefly', event.target.checked)}
                      />
                      <div className="resource-meta">
                        {item.firefly_budget_id && <small>Budget #{item.firefly_budget_id}</small>}
                        {item.firefly_bill_id && <small>Bill #{item.firefly_bill_id}</small>}
                      </div>
                    </td>
                  )}
                  <td>
                    <button type="button" className="link-button" onClick={() => onRemove(index)}>
                      <DeleteOutlineIcon fontSize="small" /> Удалить
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="empty-cell">
                  Пока нет строк в этом разделе.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MonthRangePicker({ startMonth, endMonth, onChange }) {
  const months = monthOptions;
  const handleClick = (monthValue) => {
    if (startMonth == null) {
      onChange(monthValue, null);
    } else if (endMonth == null) {
      if (monthValue >= startMonth) {
        onChange(startMonth, monthValue);
      } else {
        onChange(monthValue, null);
      }
    } else {
      onChange(monthValue, null);
    }
  };

  const isSelected = (monthValue) => {
    if (startMonth == null) return false;
    if (endMonth == null) return monthValue === startMonth;
    return monthValue >= startMonth && monthValue <= endMonth;
  };

  const rangeLabel = () => {
    if (startMonth == null) return 'Выберите начало';
    if (endMonth == null) return `С ${months[startMonth - 1]?.label?.slice(0, 3)} — выберите конец`;
    return `${months[startMonth - 1]?.label?.slice(0, 3)} – ${months[endMonth - 1]?.label?.slice(0, 3)}`;
  };

  return (
    <div className="month-range-picker">
      <div className="month-range-label">{rangeLabel()}</div>
      <div className="month-range-grid">
        {months.map((m) => (
          <button
            key={m.value}
            type="button"
            className={`month-range-cell ${isSelected(m.value) ? 'selected' : ''} ${startMonth === m.value && endMonth == null ? 'range-start' : ''} ${endMonth === m.value ? 'range-end' : ''}`}
            onClick={() => handleClick(m.value)}
          >
            {m.label.slice(0, 3)}
          </button>
        ))}
      </div>
      <button type="button" className="link-button" onClick={() => onChange(null, null)}>
        Сбросить
      </button>
    </div>
  );
}

function IncomePlanFactSummary({ summaries, currencyCode = 'RUB' }) {
  const incomeRows = summaries.filter((item) => item.kind === 'income');
  if (incomeRows.length === 0) {
    return null;
  }
  const hasBonus = incomeRows.some((item) => item.income_type === 'bonus');
  const typeLabels = { salary: 'Зарплата', bonus: 'Премия', other: 'Другое' };
  return (
    <section className="panel income-summary-panel">
      <div className="panel-header">
        <div>
          <p className="panel-kicker">Доходы</p>
          <h2>План-факт по доходам</h2>
        </div>
      </div>
      {hasBonus && (
        <div className="income-info-banner">
          Премия показывается отдельной строкой в выбранном месяце. Ее плановая сумма выделяется из общего фактического поступления на тот же счет, а остаток относится к зарплате.
        </div>
      )}
      <div className="table-scroll">
        <table className="planner-table income-summary-table">
          <thead>
            <tr>
              <th>Пользователь</th>
              <th>Доход</th>
              <th>Тип</th>
              <th>План</th>
              <th>Факт</th>
              <th>Отклонение</th>
            </tr>
          </thead>
          <tbody>
            {incomeRows.map((item) => {
              const lag = Number(item.actual_total || 0) < Number(item.planned_total || 0);
              return (
                <tr key={item.item_id} className={lag ? 'row-income-lag' : ''}>
                  <td>{item.income_owner || 'Не указан'}</td>
                  <td>{item.name}</td>
                  <td>{typeLabels[item.income_type] || 'Другое'}</td>
                  <td>{formatMoney(item.planned_total, currencyCode)}</td>
                  <td className={lag ? 'risk-value' : ''}>{formatMoney(item.actual_total, currencyCode)}</td>
                  <td className={lag ? 'risk-value' : ''}>
                    {formatMoney(item.variance, currencyCode)} {lag && <span className="risk-pill">Ниже плана</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="planfact-total-row">
              <td colSpan="3"><strong>Итого за год</strong></td>
              <td><strong>{formatMoney(incomeRows.reduce((sum, item) => sum + Number(item.planned_total || 0), 0), currencyCode)}</strong></td>
              <td><strong>{formatMoney(incomeRows.reduce((sum, item) => sum + Number(item.actual_total || 0), 0), currencyCode)}</strong></td>
              <td><strong>{formatMoney(incomeRows.reduce((sum, item) => sum + Number(item.variance || 0), 0), currencyCode)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function MonthlyPlanTable({ rows, currencyCode = 'RUB' }) {
  if (!rows.length) {
    return <p className="empty-state">После сохранения здесь появится помесячная аналитика.</p>;
  }
  const yearlyTotals = rows.reduce(
    (acc, row) => ({
      planned_income: acc.planned_income + Number(row.planned_income || 0),
      planned_expenses: acc.planned_expenses + Number(row.planned_expenses || 0),
      planned_balance: acc.planned_balance + Number(row.planned_balance || 0),
      actual_income: acc.actual_income + Number(row.actual_income || 0),
      actual_expenses: acc.actual_expenses + Number(row.actual_expenses || 0),
      actual_balance: acc.actual_balance + Number(row.actual_balance || 0),
      balance_variance: acc.balance_variance + Number(row.balance_variance || 0),
    }),
    { planned_income: 0, planned_expenses: 0, planned_balance: 0, actual_income: 0, actual_expenses: 0, actual_balance: 0, balance_variance: 0 },
  );
  return (
    <div className="table-scroll">
      <table className="planner-table">
        <thead>
          <tr>
            <th>Месяц</th>
            <th>План доход</th>
            <th>План расход</th>
            <th>План баланс</th>
            <th>Факт доход</th>
            <th>Факт расход</th>
            <th>Факт баланс</th>
            <th>Отклонение</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const expenseOverrun = Number(row.actual_expenses || 0) > Number(row.planned_expenses || 0);
            const incomeLag = Number(row.actual_income || 0) < Number(row.planned_income || 0);
            const balanceOverrun = Number(row.balance_variance || 0) < 0;
            return (
              <tr
                key={row.month}
                className={[
                  expenseOverrun ? 'row-expense-overrun' : '',
                  incomeLag ? 'row-income-lag' : '',
                  balanceOverrun ? 'row-balance-risk' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <td>
                  {monthOptions.find((month) => month.value === row.month)?.label || row.label}
                  {(expenseOverrun || incomeLag) && <span className="risk-pill">Вне плана</span>}
                </td>
                <td>{formatMoney(row.planned_income, currencyCode)}</td>
                <td>{formatMoney(row.planned_expenses, currencyCode)}</td>
                <td>{formatMoney(row.planned_balance, currencyCode)}</td>
                <td className={incomeLag ? 'risk-value' : ''}>{formatMoney(row.actual_income, currencyCode)}</td>
                <td className={expenseOverrun ? 'risk-value' : ''}>{formatMoney(row.actual_expenses, currencyCode)}</td>
                <td>{formatMoney(row.actual_balance, currencyCode)}</td>
                <td className={balanceOverrun ? 'risk-value' : 'good-value'}>{formatMoney(row.balance_variance, currencyCode)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="planfact-total-row">
            <td><strong>Итого за год</strong></td>
            <td><strong>{formatMoney(yearlyTotals.planned_income, currencyCode)}</strong></td>
            <td><strong>{formatMoney(yearlyTotals.planned_expenses, currencyCode)}</strong></td>
            <td className={yearlyTotals.planned_balance < 0 ? 'risk-value' : 'good-value'}>
              <strong>{formatMoney(yearlyTotals.planned_balance, currencyCode)}</strong>
            </td>
            <td><strong>{formatMoney(yearlyTotals.actual_income, currencyCode)}</strong></td>
            <td><strong>{formatMoney(yearlyTotals.actual_expenses, currencyCode)}</strong></td>
            <td className={yearlyTotals.actual_balance < 0 ? 'risk-value' : 'good-value'}>
              <strong>{formatMoney(yearlyTotals.actual_balance, currencyCode)}</strong>
            </td>
            <td className={yearlyTotals.balance_variance < 0 ? 'risk-value' : 'good-value'}>
              <strong>{formatMoney(yearlyTotals.balance_variance, currencyCode)}</strong>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function PlanLineChart({ rows, currencyCode = 'RUB' }) {
  const [selectedPoint, setSelectedPoint] = React.useState(null);

  if (!rows.length) {
    return <p className="empty-state">После сохранения здесь появится график плановых доходов и расходов.</p>;
  }

  const maxValue = Math.max(
    1,
    ...rows.flatMap((row) => [
      Number(row.planned_income || 0),
      Number(row.planned_expenses || 0),
    ]),
  );

  const padLeft = 55;
  const padRight = 20;
  const padTop = 30;
  const padBottom = 40;
  const width = 700;
  const height = 420;
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;

  // X = month (horizontal), Y = amount (vertical, 0 at bottom)
  const x = (month) => padLeft + ((month - 1) / 11) * chartW;
  const y = (value) => padTop + chartH - (value / maxValue) * chartH;

  const incomePoints = rows
    .map((row) => `${x(row.month)},${y(Number(row.planned_income || 0))}`)
    .join(' ');
  const expensePoints = rows
    .map((row) => `${x(row.month)},${y(Number(row.planned_expenses || 0))}`)
    .join(' ');

  // Horizontal grid lines at amount intervals
  const gridLines = [];
  for (let i = 0; i <= 4; i++) {
    const val = (maxValue / 4) * i;
    const gy = y(val);
    gridLines.push(
      <g key={`grid-${i}`}>
        <line x1={padLeft} y1={gy} x2={padLeft + chartW} y2={gy} stroke="#e0e6e9" strokeWidth="1" />
        <text x={padLeft - 8} y={gy + 4} textAnchor="end" fontSize="11" fill="#78909c">
          {formatMoney(val, currencyCode)}
        </text>
      </g>,
    );
  }

  // Month labels at bottom
  const monthLabels = rows.map((row) => {
    const label = monthOptions.find((m) => m.value === row.month)?.label || row.label;
    const shortLabel = label.slice(0, 3);
    return (
      <text key={`label-${row.month}`} x={x(row.month)} y={padTop + chartH + 16} textAnchor="middle" fontSize="12" fill="#37474f">
        {shortLabel}
      </text>
    );
  });

  return (
    <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', maxWidth: `${width}px`, height: 'auto' }}>
        {gridLines}
        {monthLabels}

        <polyline
          points={incomePoints}
          fill="none"
          stroke="#26a69a"
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        <polyline
          points={expensePoints}
          fill="none"
          stroke="#ef5350"
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        <rect x={padLeft} y={padTop} width={chartW} height={chartH} fill="transparent" onClick={() => setSelectedPoint(null)} />

        {rows.map((row) => {
          const incomeVal = Number(row.planned_income || 0);
          const expenseVal = Number(row.planned_expenses || 0);
          const label = monthOptions.find((m) => m.value === row.month)?.label || row.label;
          return (
            <g key={row.month}>
              <circle
                cx={x(row.month)}
                cy={y(incomeVal)}
                r="4"
                fill="#26a69a"
                stroke="#fff"
                strokeWidth="1.5"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPoint({ month: row.month, label, incomeVal, expenseVal });
                }}
              >
                <title>{`${label}: доходы ${formatMoney(incomeVal, currencyCode)}`}</title>
              </circle>
              <circle
                cx={x(row.month)}
                cy={y(expenseVal)}
                r="4"
                fill="#ef5350"
                stroke="#fff"
                strokeWidth="1.5"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPoint({ month: row.month, label, incomeVal, expenseVal });
                }}
              >
                <title>{`${label}: расходы ${formatMoney(expenseVal, currencyCode)}`}</title>
              </circle>
            </g>
          );
        })}

        {selectedPoint && (() => {
          const tooltipWidth = 170;
          const tooltipLines = [
            selectedPoint.label,
            `Доходы: ${formatMoney(selectedPoint.incomeVal, currencyCode)}`,
            `Расходы: ${formatMoney(selectedPoint.expenseVal, currencyCode)}`,
          ];
          const lineH = 16;
          const tooltipHeight = tooltipLines.length * lineH + 8;

          let tx = x(selectedPoint.month) + 12;
          if (tx + tooltipWidth > padLeft + chartW) {
            tx = x(selectedPoint.month) - tooltipWidth - 12;
          }
          tx = Math.max(padLeft, tx);

          const ty = Math.min(
            y(Math.max(selectedPoint.incomeVal, selectedPoint.expenseVal)),
            y(0) - 60,
          );

          return (
            <g>
              <rect x={tx} y={ty} width={tooltipWidth} height={tooltipHeight} rx="4" fill="#fff" stroke="#cfd8dc" strokeWidth="1" />
              {tooltipLines.map((line, i) => (
                <text key={i} x={tx + 6} y={ty + 14 + i * lineH} fontSize="12" fill="#37474f">{line}</text>
              ))}
            </g>
          );
        })()}

        <g transform={`translate(${padLeft + chartW - 150}, ${padTop - 20})`}>
          <line x1="0" y1="0" x2="20" y2="0" stroke="#26a69a" strokeWidth="2.5" />
          <text x="26" y="4" fontSize="12" fill="#37474f">Доходы</text>
          <line x1="90" y1="0" x2="110" y2="0" stroke="#ef5350" strokeWidth="2.5" />
          <text x="116" y="4" fontSize="12" fill="#37474f">Расходы</text>
        </g>
      </svg>
    </div>
  );
}

function CashflowChart({ rows, currencyCode = 'RUB' }) {
  if (!rows.length) {
    return <p className="empty-state">После сохранения бюджета здесь появится график приходов и расходов.</p>;
  }
  const maxValue = Math.max(
    1,
    ...rows.flatMap((row) => [
      Number(row.planned_income || 0),
      Number(row.planned_expenses || 0),
      Number(row.actual_income || 0),
      Number(row.actual_expenses || 0),
    ]),
  );
  return (
    <div className="cashflow-chart">
      {rows.map((row) => {
        const label = monthOptions.find((month) => month.value === row.month)?.label?.slice(0, 3) || row.label;
        return (
          <div key={row.month} className="cashflow-month">
            <div className="cashflow-bars" title={`${label}: план/факт`}>
              <span
                className="bar income-plan chart-tooltip"
                data-tooltip={`${label}: плановый доход ${formatMoney(row.planned_income, currencyCode)}`}
                style={{ height: `${barHeight(row.planned_income, maxValue)}%` }}
              />
              <span
                className="bar income-actual chart-tooltip"
                data-tooltip={`${label}: фактический доход ${formatMoney(row.actual_income, currencyCode)}`}
                style={{ height: `${barHeight(row.actual_income, maxValue)}%` }}
              />
              <span
                className="bar expense-plan chart-tooltip"
                data-tooltip={`${label}: плановые расходы ${formatMoney(row.planned_expenses, currencyCode)}`}
                style={{ height: `${barHeight(row.planned_expenses, maxValue)}%` }}
              />
              <span
                className="bar expense-actual chart-tooltip"
                data-tooltip={`${label}: фактические расходы ${formatMoney(row.actual_expenses, currencyCode)}`}
                style={{ height: `${barHeight(row.actual_expenses, maxValue)}%` }}
              />
            </div>
            <span className="chart-label">{label}</span>
          </div>
        );
      })}
      <div className="chart-legend">
        <span><i className="legend-dot income-plan" /> План доход</span>
        <span><i className="legend-dot income-actual" /> Факт доход</span>
        <span><i className="legend-dot expense-plan" /> План расход</span>
        <span><i className="legend-dot expense-actual" /> Факт расход</span>
      </div>
    </div>
  );
}

function MonthlyAnalyticsCharts({ rows }) {
  if (!rows.length) {
    return null;
  }
  return (
    <div className="analytics-grid">
      <article className="mini-chart-card">
        <h3>Отклонение расходов</h3>
        <VarianceStrip rows={rows} valueKey="expense_variance" reverse />
      </article>
      <article className="mini-chart-card">
        <h3>Остаток по месяцам</h3>
        <VarianceStrip rows={rows} valueKey="balance_variance" />
      </article>
    </div>
  );
}

function VarianceStrip({ rows, valueKey, reverse = false }) {
  const maxValue = Math.max(1, ...rows.map((row) => Math.abs(Number(row[valueKey] || 0))));
  return (
    <div className="variance-strip">
      {rows.map((row) => {
        const value = Number(row[valueKey] || 0);
        const isRisk = reverse ? value > 0 : value < 0;
        const label = monthOptions.find((month) => month.value === row.month)?.label?.slice(0, 3) || row.label;
        return (
          <div key={`${valueKey}-${row.month}`} className="variance-column">
            <span
              className={`variance-bar chart-tooltip ${isRisk ? 'risk' : 'ok'}`}
              style={{ height: `${barHeight(Math.abs(value), maxValue)}%` }}
              data-tooltip={`${label}: ${formatMoney(value)}`}
            />
            <small>{label}</small>
          </div>
        );
      })}
    </div>
  );
}

function BudgetPulse({ stats, rows, summaries, currencyCode = 'RUB' }) {
  const hasActualData = (stats.actualExpense || 0) > 0 || (stats.actualIncome || 0) > 0;

  const lastDataMonth = rows.reduce((latest, row) => {
    const hasData = Number(row.actual_income || 0) > 0 || Number(row.actual_expenses || 0) > 0;
    if (hasData && row.month > latest) return row.month;
    return latest;
  }, 0);
  const currentMonth = new Date().getMonth() + 1;
  const monthsSinceData = lastDataMonth > 0 && lastDataMonth < currentMonth ? currentMonth - lastDataMonth : 0;

  if (!hasActualData) {
    return (
      <article className="panel budget-pulse">
        <div>
          <p className="panel-kicker">Сводка</p>
          <h2>Пульс бюджета</h2>
        </div>
        <p className="empty-state">Нет фактических данных. Выполните синхронизацию с Firefly III или добавьте данные вручную.</p>
      </article>
    );
  }

  const plannedExpense = Math.max(1, stats.plannedExpense);
  const expenseRatio = Math.min(140, (stats.actualExpense / plannedExpense) * 100);
  const balanceTone = stats.actualBalance >= 0 ? 'ok' : 'risk';

  const expenseSummaries = (summaries || []).filter(
    (s) => s.kind === 'recurring' || s.kind === 'one_time' || s.kind === 'limit',
  );
  let expenseBreakdown = null;
  if (expenseSummaries.length > 0) {
    const { typeTotals, typeLabels } = computeExpenseAnalytics(summaries);
    expenseBreakdown = { typeTotals, typeLabels };
  }

  return (
    <article className="panel budget-pulse">
      <div>
        <p className="panel-kicker">Сводка</p>
        <h2>Пульс бюджета</h2>
      </div>
      {monthsSinceData > 0 && (
        <p className="empty-state">
          Данные не обновлялись {monthsSinceData} {monthsSinceData === 1 ? 'месяц' : monthsSinceData < 5 ? 'месяца' : 'месяцев'}. Последние фактические данные — за {monthOptions.find((m) => m.value === lastDataMonth)?.label || lastDataMonth}.
        </p>
      )}
      <div className="pulse-meter">
        <span style={{ width: `${Math.min(100, expenseRatio)}%` }} className={expenseRatio > 100 ? 'risk' : ''} />
      </div>
      <div className="pulse-grid">
        <span>Расходы к плану</span>
        <strong className={expenseRatio > 100 ? 'risk-value' : ''}>{expenseRatio.toFixed(0)}%</strong>
        <span>Факт остаток</span>
        <strong className={balanceTone === 'risk' ? 'risk-value' : 'good-value'}>{formatMoney(stats.actualBalance, currencyCode)}</strong>
        <span>Месяцев с перерасходом</span>
        <strong>{stats.expenseOverrunMonths} из {rows.length}</strong>
      </div>
      {expenseBreakdown && (
        <div className="pulse-breakdown">
          <h4>Расходы по типам</h4>
          <div className="pulse-breakdown-grid">
            {['recurring', 'one_time', 'limit'].map((kind) => {
              const t = expenseBreakdown.typeTotals[kind];
              const pct = plannedExpense > 0 ? ((t.planned / plannedExpense) * 100).toFixed(0) : 0;
              const overrun = t.actual > t.planned;
              return (
                <div key={kind} className="pulse-breakdown-item">
                  <span className="pulse-breakdown-label">{expenseBreakdown.typeLabels[kind]}</span>
                  <div className="pulse-breakdown-bar">
                    <span
                      className={`pulse-breakdown-fill ${overrun ? 'risk' : ''}`}
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                  <span className={`pulse-breakdown-value ${overrun ? 'risk-value' : ''}`}>
                    {formatMoney(t.actual, currencyCode)} / {formatMoney(t.planned, currencyCode)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </article>
  );
}

function BudgetChecklist({ stats, rows, syncItems, currencyCode = 'RUB' }) {
  const hasActualData = (stats.actualExpense || 0) > 0 || (stats.actualIncome || 0) > 0;
  const worstMonthLabel = stats.worstMonth
    ? monthOptions.find((month) => month.value === stats.worstMonth.month)?.label || stats.worstMonth.label
    : 'нет данных';

  if (!hasActualData) {
    return (
      <div className="checklist">
        <div className="check-item">
          <strong>Нет фактических данных</strong>
          <span>Выполните синхронизацию с Firefly III или добавьте данные вручную.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="checklist">
      <div className={stats.expenseOverrunMonths > 0 ? 'check-item risk' : 'check-item ok'}>
        <strong>{stats.expenseOverrunMonths > 0 ? 'Есть перерасход' : 'Расходы в плане'}</strong>
        <span>{stats.expenseOverrunMonths} месяцев выше плановых расходов</span>
      </div>
      <div className={stats.incomeLagMonths > 0 ? 'check-item risk' : 'check-item ok'}>
        <strong>{stats.incomeLagMonths > 0 ? 'Доходы отстают' : 'Доходы без просадки'}</strong>
        <span>{stats.incomeLagMonths} месяцев ниже планового дохода</span>
      </div>
      <div className="check-item">
        <strong>Худший месяц: {worstMonthLabel}</strong>
        <span>Отклонение баланса {formatMoney(stats.worstMonth?.balance_variance || 0, currencyCode)}</span>
      </div>
      <div className="check-item">
        <strong>К синхронизации: {syncItems}</strong>
        <span>Строки с включенным sync_to_firefly</span>
      </div>
    </div>
  );
}

function barHeight(value, maxValue) {
  return Math.max(4, (Number(value || 0) / maxValue) * 100);
}

function StatCard({ title, value, tone, currencyCode = 'RUB' }) {
  return (
    <article className={`stat-card ${tone}`}>
      <p>{title}</p>
      <strong>{formatMoney(value, currencyCode)}</strong>
    </article>
  );
}
